import { CommonModule } from '@angular/common';
import { Component, Injectable, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats, NativeDateAdapter } from '@angular/material/core';
import { MatDatepickerIntl, MatDatepickerModule } from '@angular/material/datepicker';

import { spParts, spToday, spWallTimeToDate } from 'src/app/models/SendWindow';

/**
 * Data (e hora) em pt-BR, independente do idioma do navegador (17tm466frtx, Bruno 02/11): o
 * `input type=date/datetime-local` nativo segue o idioma do navegador e mostrava "Clear"/"Today" e
 * hora AM/PM. Aqui o calendário é o `MatDatepicker` do `MaterialModule` (locale pt-BR), o texto é
 * dd/mm/aaaa, a hora vem em dois selects de 24 h e tudo é hora de parede de Brasília, como o resto
 * do envio — o valor do CVA é o instante absoluto (`Date | null`), certo em qualquer fuso do navegador.
 */

/** Calendário nativo do Material, mas lendo o que se digita como dd/mm/aaaa (o nativo lê mm/dd). */
@Injectable()
export class PtBrDateAdapter extends NativeDateAdapter {
  override parse(value: unknown, parseFormat?: unknown): Date | null {
    if (typeof value !== 'string') {
      return super.parse(value, parseFormat);
    }
    const text = value.trim();
    if (!text) {
      return null;
    }
    const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) {
      return this.invalid();
    }
    const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const date = new Date(year, month - 1, day);
    return date.getMonth() === month - 1 && date.getDate() === day ? date : this.invalid();
  }

  override getFirstDayOfWeek(): number {
    return 0;
  }
}

export const PT_BR_DATE_FORMATS: MatDateFormats = {
  parse: { dateInput: null },
  display: {
    dateInput: { year: 'numeric', month: '2-digit', day: '2-digit' },
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};

/** Rótulos do calendário (leitores de tela e botões do cabeçalho), que o Material só traz em inglês. */
@Injectable()
export class PtBrDatepickerIntl extends MatDatepickerIntl {
  override calendarLabel = 'Calendário';
  override openCalendarLabel = 'Abrir calendário';
  override closeCalendarLabel = 'Fechar calendário';
  override prevMonthLabel = 'Mês anterior';
  override nextMonthLabel = 'Próximo mês';
  override prevYearLabel = 'Ano anterior';
  override nextYearLabel = 'Próximo ano';
  override prevMultiYearLabel = 'Anos anteriores';
  override nextMultiYearLabel = 'Próximos anos';
  override switchToMonthViewLabel = 'Escolher data';
  override switchToMultiYearViewLabel = 'Escolher mês e ano';
  override startDateLabel = 'Data inicial';
  override endDateLabel = 'Data final';
}

interface TimeOption {
  value: string;
  disabled: boolean;
}

const pad = (n: number): string => String(n).padStart(2, '0');

/** Dia (`aaaa-mm-dd`) e minuto do dia de um instante, em Brasília. */
function spDayAndMinute(date: Date): { day: string; minute: number } {
  const { year, month, day, hour, minute } = spParts(date);
  return { day: `${year}-${pad(month)}-${pad(day)}`, minute: hour * 60 + minute };
}

/** Instante → `dd/mm/aaaa HH:mm` em Brasília. */
export function formatSpDateTime(date: Date | null): string {
  if (!date || isNaN(date.getTime())) {
    return '';
  }
  const { year, month, day, hour, minute } = spParts(date);
  return `${pad(day)}/${pad(month)}/${year} ${pad(hour)}:${pad(minute)}`;
}

/** Hora sugerida ao escolher um dia sem hora: 09:00, ou o primeiro minuto livre se o dia for o de hoje. */
const DEFAULT_MINUTE = 9 * 60;

@Component({
  selector: 'app-br-datetime-input',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDatepickerModule],
  templateUrl: './br-datetime-input.component.html',
  styleUrls: ['./br-datetime-input.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => BrDatetimeInputComponent), multi: true },
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: PtBrDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: PT_BR_DATE_FORMATS },
    { provide: MatDatepickerIntl, useClass: PtBrDatepickerIntl },
  ],
})
export class BrDatetimeInputComponent implements ControlValueAccessor {
  @Input() inputId?: string;
  @Input() testId?: string;
  /** Mostra os selects de hora (24 h). Sem eles, o valor é a meia-noite de Brasília do dia escolhido. */
  @Input() showTime = true;

  /**
   * Primeiro instante aceito (ex. agora). Dias e horas antes dele ficam bloqueados. Pode chegar um `Date`
   * novo a cada detecção de mudanças: o `Date` do calendário só é trocado quando o dia muda.
   */
  @Input() set min(value: Date | null) {
    const previous = this.minDay?.day;
    this.minDay = value ? spDayAndMinute(value) : null;
    if (this.minDay?.day !== previous) {
      this.minCalendar = this.minDay ? this.calendarOf(this.minDay.day) : null;
    }
  }

  /** Último instante aceito (ex. o teto de 7 dias do agendamento RCS). */
  @Input() set max(value: Date | null) {
    const previous = this.maxDay?.day;
    this.maxDay = value ? spDayAndMinute(value) : null;
    if (this.maxDay?.day !== previous) {
      this.maxCalendar = this.maxDay ? this.calendarOf(this.maxDay.day) : null;
    }
  }

  disabled = false;
  datePart = '';
  hour = '';
  minute = '';
  /** Dia escolhido como `Date` local do calendário: só muda junto com `datePart`, para não reescrever o que se digita. */
  calendarValue: Date | null = null;
  minCalendar: Date | null = null;
  maxCalendar: Date | null = null;

  private minDay: { day: string; minute: number } | null = null;
  private maxDay: { day: string; minute: number } | null = null;
  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};

  readonly hours = Array.from({ length: 24 }, (_, h) => pad(h));
  readonly minutes = Array.from({ length: 60 }, (_, m) => pad(m));

  get hourOptions(): TimeOption[] {
    return this.hours.map(value => ({
      value,
      disabled: !this.minutes.some(minute => this.isAllowed(this.datePart, Number(value) * 60 + Number(minute))),
    }));
  }

  get minuteOptions(): TimeOption[] {
    const hour = Number(this.hour || 0);
    return this.minutes.map(value => ({ value, disabled: !this.isAllowed(this.datePart, hour * 60 + Number(value)) }));
  }

  get todayDisabled(): boolean {
    return this.firstAllowedMinute(spToday()) === null;
  }

  // --- ControlValueAccessor ---------------------------------------------

  writeValue(value: Date | string | null): void {
    const date = value instanceof Date ? value : value ? new Date(value) : null;
    if (!date || isNaN(date.getTime())) {
      this.setDay('');
      this.hour = '';
      this.minute = '';
      return;
    }
    const { year, month, day, hour, minute } = spParts(date);
    this.setDay(`${year}-${pad(month)}-${pad(day)}`);
    this.hour = pad(hour);
    this.minute = pad(minute);
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // --- Interação -----------------------------------------------------------

  /** Dia escolhido no calendário ou digitado (já lido como dd/mm/aaaa pelo adapter). */
  onCalendarChange(date: Date | null): void {
    if (!date || isNaN(date.getTime())) {
      this.clear();
      return;
    }
    this.pickDay(`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`);
  }

  pickToday(): void {
    this.pickDay(spToday());
  }

  clear(): void {
    this.setDay('');
    this.hour = '';
    this.minute = '';
    this.emit();
  }

  onHourChange(hour: string): void {
    this.hour = hour;
    const base = Number(hour) * 60;
    if (!this.isAllowed(this.datePart, base + Number(this.minute || 0))) {
      const firstMinute = this.minutes.find(minute => this.isAllowed(this.datePart, base + Number(minute)));
      this.minute = firstMinute ?? '00';
    }
    this.emit();
  }

  onMinuteChange(minute: string): void {
    this.minute = minute;
    this.emit();
  }

  trackByValue(_index: number, option: TimeOption): string {
    return option.value;
  }

  /** Troca o dia mantendo a hora escolhida quando ela vale no dia novo; senão sugere a primeira livre. */
  private pickDay(day: string): void {
    this.setDay(day);
    const current = this.hour && this.minute ? Number(this.hour) * 60 + Number(this.minute) : null;
    let minute: number | null;
    if (current !== null && this.isAllowed(day, current)) {
      minute = current;
    } else if (this.minDay?.day !== day && this.isAllowed(day, DEFAULT_MINUTE)) {
      minute = DEFAULT_MINUTE;
    } else {
      minute = this.firstAllowedMinute(day);
    }
    if (minute === null) {
      this.clear();
      return;
    }
    this.hour = pad(Math.floor(minute / 60));
    this.minute = pad(minute % 60);
    this.emit();
  }

  private setDay(day: string): void {
    this.datePart = day;
    this.calendarValue = day ? this.calendarOf(day) : null;
  }

  private calendarOf(day: string): Date {
    const [year, month, date] = day.split('-').map(Number);
    return new Date(year, month - 1, date);
  }

  /** Primeiro minuto aceito no dia, já com 1 minuto de folga quando o dia é o do `min` (agora). */
  private firstAllowedMinute(day: string): number | null {
    for (let minute = 0; minute < 24 * 60; minute++) {
      if (this.isAllowed(day, minute) && (this.minDay?.day !== day || minute > this.minDay.minute)) {
        return minute;
      }
    }
    return null;
  }

  private isAllowed(day: string, minute: number): boolean {
    if (!day) {
      return true;
    }
    if (this.minDay && (day < this.minDay.day || (day === this.minDay.day && minute < this.minDay.minute))) {
      return false;
    }
    if (this.maxDay && (day > this.maxDay.day || (day === this.maxDay.day && minute > this.maxDay.minute))) {
      return false;
    }
    return true;
  }

  private emit(): void {
    this.onTouched();
    if (!this.datePart) {
      this.onChange(null);
      return;
    }
    this.onChange(spWallTimeToDate(this.datePart, this.showTime ? `${this.hour || '00'}:${this.minute || '00'}` : '00:00'));
  }
}
